import { deviceLocal, deviceSession } from '@/privacy/deviceStorage';
import { makeObservable, observable, computed, action, runInAction } from 'mobx';
import type { ISavedView, ViewLayoutType } from '@featit/shared';
import { VIEW_LAYOUTS } from '@featit/shared/types/index';
import { savedViewDataSourceRegistry } from '@/data-sources/SavedViewDataSourceRegistry';
import { ViewHierarchyStore } from './ViewHierarchyStore';

const LAYOUT_KEY = 'featit:currentLayout';

export class ViewStore {
  currentLayout: ViewLayoutType =
    (deviceSession.getItem(LAYOUT_KEY) as ViewLayoutType) || VIEW_LAYOUTS.LIST;
  /** The work area `savedViews` were read for, or `null` before the first read (I-0958). */
  workAreaId: string | null = null;
  activeViewId: string | null = null;
  filters: Record<string, unknown> = {};
  sortBy = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  constructor(
    /**
     * F-0363 slice 2, AC-16: a write made through this store (`SavedViewSelector.tsx`'s own
     * path) nudges `ViewHierarchyStore`'s copy of the same work area's rows. A callback rather
     * than calling `viewHierarchyStore` directly for this keeps the two decoupled the way
     * `notificationStore`/`decisionsStore` are already wired to their own cross-cutting concerns
     * in `stores/index.ts`, and is what lets a save made through `CustomViewsPage.tsx` (this
     * store's own create/update/delete flow) reach the sidebar tree without this store needing
     * to know the tree exists.
     */
    private readonly onViewsChanged?: (projectId: string) => void,
    /**
     * F-0363 slice 2, AC-19: `ViewHierarchyStore` is the one holder of a work area's saved views
     * — it already carries order, folders and the optimistic write contract AC-7 through AC-14
     * hold it to. `savedViews` and `loading` below read it directly; this store no longer keeps
     * a second, unordered copy of its own that only `fetchSavedViews` kept current (AC-16's own
     * nudge, which this replaces outright). Write actions and the composing state below are a
     * different concern and do not move.
     */
    private readonly viewHierarchyStore: ViewHierarchyStore = new ViewHierarchyStore(),
  ) {
    deviceLocal.removeItem(LAYOUT_KEY);
    makeObservable(this, {
      currentLayout: observable,
      workAreaId: observable,
      enterWorkArea: action,
      activeViewId: observable,
      filters: observable,
      sortBy: observable,
      sortOrder: observable,
      savedViews: computed,
      loading: computed,
      hasActiveFilters: computed,
      filterCount: computed,
      activeView: computed,
      setLayout: action,
      setFilters: action,
      clearFilters: action,
      setSortBy: action,
      fetchSavedViews: action,
      saveCurrentView: action,
      loadView: action,
      deleteView: action,
    });
  }

  /**
   * F-0363 slice 2, AC-19: `ViewHierarchyStore`'s own copy of this work area's rows, folders
   * filtered out — the same way `ViewNavigationList.tsx`'s own `customViewsOnly` already does.
   */
  get savedViews(): ISavedView[] {
    return this.viewHierarchyStore.views(this.workAreaId).filter((view) => view.kind !== 'folder');
  }

  get loading(): boolean {
    return this.viewHierarchyStore.isLoading(this.workAreaId);
  }

  get hasActiveFilters(): boolean {
    return Object.keys(this.filters).some(
      (key) =>
        this.filters[key] !== undefined && this.filters[key] !== null && this.filters[key] !== '',
    );
  }

  get filterCount(): number {
    return Object.keys(this.filters).filter(
      (key) =>
        this.filters[key] !== undefined && this.filters[key] !== null && this.filters[key] !== '',
    ).length;
  }

  get activeView(): ISavedView | undefined {
    if (!this.activeViewId) return undefined;
    return this.savedViews.find((v) => v.id === this.activeViewId);
  }

  setLayout(layout: ViewLayoutType): void {
    this.currentLayout = layout;
    deviceSession.setItem(LAYOUT_KEY, layout);
  }

  setFilters(filters: Record<string, unknown>): void {
    this.filters = { ...this.filters, ...filters };
    this.activeViewId = null;
  }

  clearFilters(): void {
    this.filters = {};
    this.activeViewId = null;
  }

  setSortBy(sortBy: string, sortOrder?: 'asc' | 'desc'): void {
    this.sortBy = sortBy;
    if (sortOrder) {
      this.sortOrder = sortOrder;
    } else {
      this.sortOrder = this.sortBy === sortBy && this.sortOrder === 'desc' ? 'asc' : 'desc';
    }
  }

  /** I-0958 (WS-3): another work area's saved views are dropped before its page renders. */
  enterWorkArea(workAreaId: string): void {
    if (this.workAreaId === workAreaId) return;
    this.workAreaId = null;
    this.activeViewId = null;
  }

  /**
   * F-0363 slice 2, AC-19: loads through the one holder, `ViewHierarchyStore.ensureProject` —
   * loaded or loading already → no request — rather than this store's own, now-removed `findAll`.
   * Still named and still called the way it always was: `ViewNavigationList.tsx`'s mount effect
   * calls this beside its own direct `ensureProject` call, and the two resolve to the same shared,
   * deduplicated load, so calling it from both places costs nothing extra.
   */
  async fetchSavedViews(projectId: string): Promise<void> {
    this.workAreaId = projectId;
    await this.viewHierarchyStore.ensureProject(projectId);
  }

  async saveCurrentView(projectId: string, name: string) {
    try {
      const view = await savedViewDataSourceRegistry.forProject(projectId).create(projectId, {
        name,
        layout: this.currentLayout,
        filters: { ...this.filters },
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      });
      runInAction(() => {
        this.activeViewId = view.id;
      });
      this.onViewsChanged?.(projectId);
      return view;
    } catch {
      return null;
    }
  }

  loadView(view: ISavedView): void {
    this.activeViewId = view.id;
    this.currentLayout = view.layout;
    this.filters = { ...view.filters };
    this.sortBy = view.sortBy ?? 'createdAt';
    this.sortOrder = view.sortOrder ?? 'desc';
    deviceSession.setItem(LAYOUT_KEY, view.layout);
  }

  async deleteView(projectId: string, viewId: string) {
    try {
      await savedViewDataSourceRegistry.forProject(projectId).remove(projectId, viewId);
      runInAction(() => {
        if (this.activeViewId === viewId) {
          this.activeViewId = null;
        }
      });
      this.onViewsChanged?.(projectId);
    } catch {
      // ignore
    }
  }
}
